import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as branchesApi from './branches.api';

import type { BranchListQuery, CreateBranchRequest, UpdateBranchRequest } from '@utazzo/shared';

const QK = {
  list: (query: BranchListQuery) => ['branches', 'list', query] as const,
  detail: (id: string) => ['branches', 'detail', id] as const,
  countries: ['geo', 'countries'] as const,
  states: (countryId: string) => ['geo', 'states', countryId] as const,
  cities: (countryId: string, stateId?: string) =>
    ['geo', 'cities', countryId, stateId ?? ''] as const,
};

export const useBranchesList = (query: BranchListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => branchesApi.listBranches(query),
    staleTime: 30_000,
  });

export const useCreateBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBranchRequest) => branchesApi.createBranch(payload),
    onSuccess: (branch) => {
      qc.invalidateQueries({ queryKey: ['branches', 'list'] });
      toast.success(`Added ${branch.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add branch'),
  });
};

export const useUpdateBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBranchRequest }) =>
      branchesApi.updateBranch(id, payload),
    onSuccess: (branch) => {
      qc.invalidateQueries({ queryKey: ['branches', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(branch.id) });
      toast.success('Branch updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update branch'),
  });
};

export const useDeleteBranch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => branchesApi.deleteBranch(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branches', 'list'] });
      toast.success('Branch deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete branch'),
  });
};

export const useBulkDeleteBranches = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => branchesApi.bulkDeleteBranches(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branches', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete branches'),
  });
};

export const useBulkSetBranchesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      branchesApi.bulkSetBranchesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branches', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update branch status'),
  });
};

// ----- Geography option hooks -----

export const useCountryOptions = () =>
  useQuery({
    queryKey: QK.countries,
    queryFn: branchesApi.listCountryOptions,
    // Seeded master data - rarely changes within a session.
    staleTime: 5 * 60_000,
  });

export const useStateOptions = (countryId: string | null) =>
  useQuery({
    queryKey: QK.states(countryId ?? ''),
    queryFn: () => branchesApi.listStateOptions(countryId!),
    enabled: !!countryId,
    staleTime: 5 * 60_000,
  });

export const useCityOptions = (countryId: string | null, stateId?: string | null) =>
  useQuery({
    queryKey: QK.cities(countryId ?? '', stateId ?? undefined),
    queryFn: () => branchesApi.listCityOptions(countryId!, stateId ?? undefined),
    // City list is only fetched once a country is chosen.
    enabled: !!countryId,
    staleTime: 5 * 60_000,
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
